import { Vec3, vec3, add, sub, scale, cross, dot, length, normalize } from '../math/vec3'
import { OrbitElements, elementsFromState, stateFromElements, apoapsisRadius, periapsisRadius } from './elements'
import { propagate } from './kepler'
import { tempoAAnomalia } from './prediction'

/**
 * Una manovra: quando accendere e di quanto spingere, nei tre versi che un
 * pilota usa davvero — avanti, fuori dal piano, e verso l'alto locale.
 */
export interface Manovra {
  /** fra quanti secondi da adesso */
  readonly quando: number
  readonly prograde: number
  readonly normal: number
  readonly radial: number
}

export const manovraNulla = (quando = 0): Manovra => ({ quando, prograde: 0, normal: 0, radial: 0 })

export const deltaVDi = (m: Manovra): number =>
  Math.hypot(m.prograde, m.normal, m.radial)

/** La terna del pilota nel punto dato: avanti, normale al piano, radiale in fuori. */
export function baseManovra(position: Vec3, velocity: Vec3): { prograde: Vec3; normal: Vec3; radial: Vec3 } {
  const prograde = normalize(velocity)
  const normal = normalize(cross(position, velocity))
  const radial = normalize(cross(prograde, normal))
  return { prograde, normal, radial }
}

/** L'orbita che risulta eseguendo la manovra. */
export function applicaManovra(el: OrbitElements, m: Manovra): OrbitElements {
  const al = propagate(el, m.quando)
  const s = stateFromElements(al)
  const b = baseManovra(s.position, s.velocity)
  const dv = add(
    add(scale(b.prograde, m.prograde), scale(b.normal, m.normal)),
    scale(b.radial, m.radial),
  )
  return elementsFromState(s.position, add(s.velocity, dv), el.mu, el.epoch + m.quando)
}

/** La manovra che rende circolare l'orbita nel punto indicato. */
export function manovraPerCircolarizzare(el: OrbitElements, quando: number): Manovra {
  const al = propagate(el, quando)
  const s = stateFromElements(al)
  const r = length(s.position)
  const vCirc = Math.sqrt(el.mu / r)

  const h = cross(s.position, s.velocity)
  // direzione del moto su un'orbita circolare: perpendicolare al raggio, nel piano
  const tangente = normalize(cross(h, s.position))
  const dv = sub(scale(tangente, vCirc), s.velocity)

  const b = baseManovra(s.position, s.velocity)
  return {
    quando,
    prograde: dot(dv, b.prograde),
    normal: dot(dv, b.normal),
    radial: dot(dv, b.radial),
  }
}

/**
 * La manovra che, accesa al periapside, porta l'apoapside al raggio voluto.
 * Da vis-viva: al raggio r, per avere apoapside r_a serve
 *   v' = √(μ (2/r − 2/(r + r_a)))
 */
export function manovraPerAlzareApoapside(el: OrbitElements, raggioApoapside: number): Manovra {
  const quando = tempoAAnomalia(el, 0)   // al periapside la spinta rende di piu
  const al = propagate(el, quando)
  const s = stateFromElements(al)
  const r = length(s.position)
  const vAdesso = length(s.velocity)
  const vVoluta = Math.sqrt(el.mu * (2 / r - 2 / (r + raggioApoapside)))
  return { quando, prograde: vVoluta - vAdesso, normal: 0, radial: 0 }
}

/** La manovra che porta il periapside alla quota voluta, accesa all'apoapside. */
export function manovraPerAlzarePeriapside(el: OrbitElements, raggioPeriapside: number): Manovra {
  const quando = tempoAAnomalia(el, Math.PI)
  const al = propagate(el, quando)
  const s = stateFromElements(al)
  const r = length(s.position)
  const vAdesso = length(s.velocity)
  const vVoluta = Math.sqrt(el.mu * (2 / r - 2 / (r + raggioPeriapside)))
  return { quando, prograde: vVoluta - vAdesso, normal: 0, radial: 0 }
}

/** Il vettore della manovra nel riferimento del mondo: serve al pilota e alla mappa. */
export function direzioneManovra(el: OrbitElements, m: Manovra): Vec3 {
  const s = stateFromElements(propagate(el, m.quando))
  const b = baseManovra(s.position, s.velocity)
  const dv = add(
    add(scale(b.prograde, m.prograde), scale(b.normal, m.normal)),
    scale(b.radial, m.radial),
  )
  return length(dv) > 0 ? normalize(dv) : vec3(0, 1, 0)
}
